import { HttpClient } from '@zeta-core';
import { APIResponse, IViewMISDasboardResponse } from '@zeta-types';
import { FINANCIAL_MIS_SERVICE } from '../services-path';

export const FinancialMisService = () => {
  return {
    // Client Portal
    getPortfolioGrowth: ({ clientID, fromDate, toDate }: { clientID: string; fromDate?: string; toDate?: string }) => {
      const url = `${FINANCIAL_MIS_SERVICE}/client-dashboard/client/${clientID}/portfolio`;

      const queryParams = new URLSearchParams();
      if (fromDate) {
        queryParams.append('fromDate', fromDate);
      }
      if (toDate) {
        queryParams.append('toDate', toDate);
      }

      const queryString = queryParams.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      return HttpClient.get<string[]>(finalUrl, {});
    },
    getFundAllocation: ({
      clientID,
      accountId,
      pageSize,
      pageIndex,
      sort,
      desc,
    }: {
      clientID: string;
      accountId: string;
      pageSize?: string;
      pageIndex?: string;
      sort?: string;
      desc?: boolean;
    }) => {
      const url = `${FINANCIAL_MIS_SERVICE}/client-dashboard/client/${clientID}/accounts/${accountId}/funds`;

      const queryParams = new URLSearchParams();
      if (pageSize) {
        queryParams.append('pageSize', pageSize);
      }
      if (pageIndex) {
        queryParams.append('pageIndex', pageIndex);
      }
      if (sort) {
        queryParams.append('sort', sort);
      }
      if (desc) {
        queryParams.append('desc', desc);
      }

      const queryString = queryParams.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      return HttpClient.get<string[]>(finalUrl, {});
    },
    getPortfolioAccounts: ({
      clientID,
      pageSize,
      pageIndex,
      sort,
      desc,
    }: {
      clientID: string;
      pageSize?: string;
      pageIndex?: string;
      sort?: string;
      desc?: boolean;
    }) => {
      const url = `${FINANCIAL_MIS_SERVICE}/client-dashboard/client/${clientID}/accounts`;

      const queryParams = new URLSearchParams();
      if (pageSize) {
        queryParams.append('pageSize', pageSize);
      }
      if (pageIndex) {
        queryParams.append('pageIndex', pageIndex);
      }
      if (sort) {
        queryParams.append('sort', sort);
      }
      if (desc) {
        queryParams.append('desc', desc);
      }

      const queryString = queryParams.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      return HttpClient.get<string[]>(finalUrl, {});
    },

    getPortfolioTransactions: ({
      clientID,
      accountId,
      pageSize,
      pageIndex,
      sort,
      desc,
    }: {
      clientID: string;
      accountId: string;
      pageSize?: string;
      pageIndex?: string;
      sort?: string;
      desc?: boolean;
    }) => {
      const url = `${FINANCIAL_MIS_SERVICE}/client-dashboard/client/${clientID}/accounts/${accountId}/transactions`;

      const queryParams = new URLSearchParams();
      if (pageSize) {
        queryParams.append('pageSize', pageSize);
      }
      if (pageIndex) {
        queryParams.append('pageIndex', pageIndex);
      }
      if (sort) {
        queryParams.append('sort', sort);
      }
      if (desc) {
        queryParams.append('desc', desc);
      }

      const queryString = queryParams.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      return HttpClient.get<string[]>(finalUrl, {});
    },

    getPortfolioDistributions: ({
      clientID,
      accountId,
      pageSize,
      pageIndex,
      sort,
      desc,
    }: {
      clientID: string;
      accountId: string;
      pageSize?: string;
      pageIndex?: string;
      sort?: string;
      desc?: boolean;
    }) => {
      const url = `${FINANCIAL_MIS_SERVICE}/client-dashboard/client/${clientID}/accounts/${accountId}/distributions`;

      const queryParams = new URLSearchParams();
      if (pageSize) {
        queryParams.append('pageSize', pageSize);
      }
      if (pageIndex) {
        queryParams.append('pageIndex', pageIndex);
      }
      if (sort) {
        queryParams.append('sort', sort);
      }
      if (desc) {
        queryParams.append('desc', desc);
      }

      const queryString = queryParams.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      return HttpClient.get<string[]>(finalUrl, {});
    },

    getAdvisorDetail: ({ clientID }: { clientID: string }) => {
      return HttpClient.get<string[]>(`${FINANCIAL_MIS_SERVICE}/client-dashboard/client/${clientID}/advisor-individual`, {});
    },

    getPortfolioAllocation: ({ clientID }: { clientID: string }) => {
      return HttpClient.get<string[]>(`${FINANCIAL_MIS_SERVICE}/client-dashboard/client/${clientID}/portfolio-allocation`, {});
    },

    getHoldingsWeightage: ({ clientID, accountId, category }: { clientID: string; accountId: string; category: string }) => {
      return HttpClient.get<string[]>(`${FINANCIAL_MIS_SERVICE}/client-dashboard/client/${clientID}/accounts/${accountId}/holdings-breakdown/${category}`, {});
    },

    getInvestmentGoalTracker: ({ clientID, fromDate, toDate }: { clientID: string; fromDate?: string; toDate?: string }) => {
      return HttpClient.get<string[]>(`${FINANCIAL_MIS_SERVICE}/client-dashboard/client/${clientID}/investment-planning-goals`, {});
    },
    viewMisDashboard: (clientId: string): APIResponse<IViewMISDasboardResponse> => HttpClient.get(`${FINANCIAL_MIS_SERVICE}/api/${clientId}/mis-dashboard`),
    viewMisDashboardClientGrowth: (clientId: string, fromDate: string, toDate: string): APIResponse<IViewMISDasboardResponse> =>
      HttpClient.get(`${FINANCIAL_MIS_SERVICE}/api/${clientId}/client-growth?fromDate=${fromDate}&toDate=${toDate}`),
    viewMisDashboardPortfolioGrowth: (clientId: string, fromDate: string, toDate: string): APIResponse<IViewMISDasboardResponse> =>
      HttpClient.get(`${FINANCIAL_MIS_SERVICE}/api/${clientId}/portfolio-growth?fromDate=${fromDate}&toDate=${toDate}`),

    // Advisor Portal
    getPortfolioGrowthAdvisor: ({ clientID, fromDate, toDate }: { clientID: string; fromDate?: string; toDate?: string }) => {
      const url = `${FINANCIAL_MIS_SERVICE}/advisor/clients/${clientID}/portfolio`;

      const queryParams = new URLSearchParams();
      if (fromDate) {
        queryParams.append('fromDate', fromDate);
      }
      if (toDate) {
        queryParams.append('toDate', toDate);
      }

      const queryString = queryParams.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      return HttpClient.get<string[]>(finalUrl, {});
    },

    getFundAllocationAdvisor: ({
      clientID,
      accountId,
      pageSize,
      pageIndex,
      sort,
      desc,
    }: {
      clientID: string;
      accountId: string;
      pageSize?: string;
      pageIndex?: string;
      sort?: string;
      desc?: boolean;
    }) => {
      const url = `${FINANCIAL_MIS_SERVICE}/advisor/clients/${clientID}/accounts/${accountId}/funds`;

      const queryParams = new URLSearchParams();
      if (pageSize) {
        queryParams.append('pageSize', pageSize);
      }
      if (pageIndex) {
        queryParams.append('pageIndex', pageIndex);
      }
      if (sort) {
        queryParams.append('sort', sort);
      }
      if (desc) {
        queryParams.append('desc', desc);
      }

      const queryString = queryParams.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      return HttpClient.get<string[]>(finalUrl, {});
    },

    getPortfolioAccountsAdvisor: ({
      clientID,
      pageSize,
      pageIndex,
      sort,
      desc,
    }: {
      clientID: string;
      pageSize?: string;
      pageIndex?: string;
      sort?: string;
      desc?: boolean;
    }) => {
      const url = `${FINANCIAL_MIS_SERVICE}/advisor/clients/${clientID}/accounts`;

      const queryParams = new URLSearchParams();
      if (pageSize) {
        queryParams.append('pageSize', pageSize);
      }
      if (pageIndex) {
        queryParams.append('pageIndex', pageIndex);
      }
      if (sort) {
        queryParams.append('sort', sort);
      }
      if (desc) {
        queryParams.append('desc', desc);
      }

      const queryString = queryParams.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      return HttpClient.get<string[]>(finalUrl, {});
    },

    getPortfolioTransactionsAdvisor: ({
      clientID,
      accountId,
      pageSize,
      pageIndex,
      sort,
      desc,
    }: {
      clientID: string;
      accountId: string;
      pageSize?: string;
      pageIndex?: string;
      sort?: string;
      desc?: boolean;
    }) => {
      const url = `${FINANCIAL_MIS_SERVICE}/advisor/clients/${clientID}/accounts/${accountId}/transactions`;

      const queryParams = new URLSearchParams();
      if (pageSize) {
        queryParams.append('pageSize', pageSize);
      }
      if (pageIndex) {
        queryParams.append('pageIndex', pageIndex);
      }
      if (sort) {
        queryParams.append('sort', sort);
      }
      if (desc) {
        queryParams.append('desc', desc);
      }

      const queryString = queryParams.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      return HttpClient.get<string[]>(finalUrl, {});
    },

    getPortfolioDistributionsAdvisor: ({
      clientID,
      accountId,
      pageSize,
      pageIndex,
      sort,
      desc,
    }: {
      clientID: string;
      accountId: string;
      pageSize?: string;
      pageIndex?: string;
      sort?: string;
      desc?: boolean;
    }) => {
      const url = `${FINANCIAL_MIS_SERVICE}/advisor/clients/${clientID}/accounts/${accountId}/distributions`;

      const queryParams = new URLSearchParams();
      if (pageSize) {
        queryParams.append('pageSize', pageSize);
      }
      if (pageIndex) {
        queryParams.append('pageIndex', pageIndex);
      }
      if (sort) {
        queryParams.append('sort', sort);
      }
      if (desc) {
        queryParams.append('desc', desc);
      }

      const queryString = queryParams.toString();
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      return HttpClient.get<string[]>(finalUrl, {});
    },

    getAdvisorDetailAdvisor: ({ clientID }: { clientID: string }) => {
      return HttpClient.get<string[]>(`${FINANCIAL_MIS_SERVICE}/advisor/advisor-individual`, {});
    },

    getPortfolioAllocationAdvisor: ({ clientID }: { clientID: string }) => {
      return HttpClient.get<string[]>(`${FINANCIAL_MIS_SERVICE}/advisor/clients/${clientID}/portfolio-allocation`, {});
    },

    getHoldingsWeightageAdvisor: ({ clientID, accountId, category }: { clientID: string; accountId: string; category: string }) => {
      return HttpClient.get<string[]>(`${FINANCIAL_MIS_SERVICE}/advisor/clients/${clientID}/accounts/${accountId}/holdings-breakdown/${category}`, {});
    },

    getInvestmentGoalTrackerAdvisor: ({ clientID, fromDate, toDate }: { clientID: string; fromDate?: string; toDate?: string }) => {
      return HttpClient.get<string[]>(`${FINANCIAL_MIS_SERVICE}/advisor/clients/${clientID}/investment-planning-goals`, {});
    },
  };
};
